import { useCallback, useEffect, useMemo } from 'react'
import { TreatmentGroupedAppointmentsComponentViewModel } from '../TreatmentGroupedAppointments'
import { AddAppointmentModalParams } from '../../../types/treatmentPanel.types'
import {
  getFilteredAppointments,
  getFilteredReferrals,
  getFilteredSurveys,
  getGroupedItems,
} from '../../../utils/treatmentItem.util'
import { useSelectedLanguage } from '../../../../../../common/hooks/useSelectedLanguage'
import dayjs from 'dayjs'
import { useArrowProps } from './useArrowProps'
import { getParentsIds } from '../utils/treatmentPanel.helper'
import { useAppointmentOperations } from './useAppointmentOperations'
import { useSurveyOperations } from './useSurveyOperations'
import { AddSurveyModalParams } from '../../Survey/AddSurveyModal'
import { Appointment, Survey } from '../../../types/treatments.types'
import { useTreatmentPanelData } from '../TreatmentPanelProvider'

interface UseTreatmentGroupedAppointmentsComponentViewModelProps {
  setSurvey: React.Dispatch<React.SetStateAction<Survey | null>>
  firstRef: React.RefObject<HTMLElement | null>
  toggleAddAppointmentModal: React.Dispatch<
    React.SetStateAction<AddAppointmentModalParams>
  >
  toggleAddSurveyModal: React.Dispatch<
    React.SetStateAction<AddSurveyModalParams>
  >
}

export const useTreatmentGroupedAppointmentsComponentViewModel = ({
  setSurvey,
  firstRef,
  toggleAddAppointmentModal,
  toggleAddSurveyModal,
}: UseTreatmentGroupedAppointmentsComponentViewModelProps): TreatmentGroupedAppointmentsComponentViewModel => {
  const {
    treatment,
    isTreatmentDeleted,
    showPastTreatmentItems,
    setShowPastTreatmentItems,
    filterValue,
    linkingDisabledIds,
    setLinkingDisabledIds,
    linkingAppointments,
    setLinkingAppointments,
  } = useTreatmentPanelData()
  const selectedLanguage = useSelectedLanguage()
  const todayStart = dayjs().startOf('day').toISOString()

  const appointments = useMemo(() => treatment.appointments || [], [treatment])
  const surveys = useMemo(() => treatment.surveys || [], [treatment])
  const referrals = useMemo(() => treatment.referrals || [], [treatment])

  useEffect(() => {
    // if treatment is deleted, show past appointments by default
    if (isTreatmentDeleted) {
      setShowPastTreatmentItems(true)
    }

    // if treatment has only past items, show past items by default
    const allItems = [
      ...appointments.map((a) => ({ ...a, date: a.proposedDate })),
      ...surveys.map((s) => ({ ...s, date: s.fillableFrom })),
      ...referrals.map((r) => ({ ...r, date: r.expirationDate })),
    ]
    const futureItems = allItems.filter((a) => todayStart < a.date)
    const hasItems = allItems.length > 0
    const hasFutureAppointments = futureItems.length > 0
    if (hasItems && !hasFutureAppointments) {
      setShowPastTreatmentItems(true)
    }
  }, [
    appointments,
    setShowPastTreatmentItems,
    todayStart,
    isTreatmentDeleted,
    surveys,
    referrals,
  ])

  const {
    groupedTreatmentItems,
    visibleAppointments,
    visibleSurveys,
    visibleReferrals,
    isNoFilteredResultContent,
    nextTreatmentItem,
  } = useMemo(() => {
    const filteredOrderedSurveys = getFilteredSurveys(surveys, filterValue)

    const filteredOrderedAppointments = getFilteredAppointments(
      appointments,
      selectedLanguage,
      filterValue
    )

    const filteredOrderedReferrals = getFilteredReferrals(
      referrals,
      filterValue
    )

    const isNoFilteredResultContent =
      filteredOrderedSurveys.length < 1 &&
      filteredOrderedAppointments.length < 1 &&
      filteredOrderedReferrals.length < 1

    const futureAppointments = filteredOrderedAppointments.filter(
      (a) => todayStart <= a.proposedDate
    )

    const futureSurveys = filteredOrderedSurveys.filter(
      (s) => todayStart <= s.fillableFrom
    )

    const futureReferrals = filteredOrderedReferrals.filter(
      (r) => todayStart <= r.expirationDate
    )

    const allFutureItems = [
      ...futureAppointments.map((a) => ({
        ...a,
        date: a.proposedDate,
      })),
      ...futureSurveys.map((s) => ({
        ...s,
        date: s.fillableFrom,
      })),
      ...futureReferrals.map((r) => ({
        ...r,
        date: r.expirationDate,
      })),
    ]

    const sortedByDate = [...allFutureItems].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    )

    const nextTreatmentItem = sortedByDate[0] ?? null

    const visibleAppointments = showPastTreatmentItems
      ? filteredOrderedAppointments
      : futureAppointments

    const visibleSurveys = showPastTreatmentItems
      ? filteredOrderedSurveys
      : futureSurveys

    const visibleReferrals = showPastTreatmentItems
      ? filteredOrderedReferrals
      : futureReferrals

    const groupedTreatmentItems = getGroupedItems(
      filteredOrderedAppointments,
      filteredOrderedSurveys,
      filteredOrderedReferrals
    )

    return {
      groupedTreatmentItems,
      visibleAppointments,
      visibleSurveys,
      visibleReferrals,
      futureAppointments,
      isNoFilteredResultContent,
      futureSurveys,
      nextTreatmentItem,
    }
  }, [
    surveys,
    appointments,
    selectedLanguage,
    filterValue,
    showPastTreatmentItems,
    referrals,
    todayStart,
  ])

  const handleLink = useCallback(
    (appointment: Appointment) => {
      const isExists = linkingAppointments.some(
        (item) => item.id === appointment.id
      )
      if (isExists) {
        setLinkingDisabledIds([])
        setLinkingAppointments([])
      } else {
        const allParents = getParentsIds(
          treatment?.appointments || [],
          appointment.id
        )
        const directParents = getParentsIds(
          treatment?.appointments || [],
          appointment.id,
          [],
          false
        )
        const disabledIds = allParents.filter(
          (id) => !directParents.includes(id)
        )
        setLinkingDisabledIds(disabledIds)
        setLinkingAppointments((prevState) => [...prevState, appointment])
      }
    },
    [
      linkingAppointments,
      setLinkingAppointments,
      setLinkingDisabledIds,
      treatment?.appointments,
    ]
  )

  const { arrowProps, widthOfArrows } = useArrowProps({
    filterValue,
    visibleAppointments,
  })

  const { openAppointment, editAppointment, cloneAppointment } =
    useAppointmentOperations({ toggleAddAppointmentModal })

  const { onOpenSurvey, onEditSurvey } = useSurveyOperations({
    setSurvey,
    patientUserId: treatment.patient.user?.id ?? '',
    toggleAddSurveyModal,
  })

  return {
    groupedTreatmentItems,
    isNoFilteredResultContent,
    nextTreatmentItem,
    widthOfArrows,
    arrowProps,
    visibleAppointments,
    visibleSurveys,
    visibleReferrals,
    linkingDisabledIds,
    onClickAppointment: openAppointment,
    onClickSurvey: onOpenSurvey,
    editSurvey: onEditSurvey,
    treatmentItemViewModel: {
      firstRef,
      linking: linkingAppointments[0],
      cloneAppointment,
      editAppointment,
      onLink: handleLink,
    },
  }
}
